import React, { useState } from 'react';
import {
  Layout,
  Card,
  Row,
  Col,
  Progress,
  Avatar,
  Button,
  Badge,
  Table,
  Tabs,
  Statistic,
  List,
  Tag,
  Menu,
  Dropdown,
  notification,
  Space,
  Typography,
  Input,
  Select
} from 'antd';
import {
  BookOutlined,
  TrophyOutlined,
  ClockCircleOutlined,
  UserOutlined,
  BellOutlined,
  SearchOutlined,
  PlayCircleOutlined,
  CheckCircleOutlined,
  StarOutlined,
  TeamOutlined,
  BarChartOutlined,
  SettingOutlined,
  LogoutOutlined
} from '@ant-design/icons';

const { Header, Content, Sider } = Layout;
const { Title, Text } = Typography;
const { TabPane } = Tabs;

const HospitalTalentDashboard = () => {
  const [selectedMenuItem, setSelectedMenuItem] = useState('dashboard');
  const [searchTerm, setSearchTerm] = useState('');

  // Sample data
  const userProfile = {
    name: "Dr. Sarah Johnson",
    role: "Senior Nurse",
    department: "Emergency Department",
    completionRate: 78,
    certifications: 12,
    avatar: null
  };

  const recentCourses = [
    {
      id: 1,
      title: "Advanced Cardiac Life Support (ACLS)",
      progress: 85,
      duration: "4 hours",
      dueDate: "2 days",
      category: "Emergency Care",
      status: "In Progress"
    },
    {
      id: 2,
      title: "Infection Control Protocols",
      progress: 100,
      duration: "2 hours",
      dueDate: "Completed",
      category: "Safety",
      status: "Completed"
    },
    {
      id: 3,
      title: "Patient Communication Skills",
      progress: 45,
      duration: "3 hours",
      dueDate: "5 days",
      category: "Soft Skills",
      status: "In Progress"
    }
  ];

  const certifications = [
    {
      id: 1,
      name: "Basic Life Support (BLS)",
      issueDate: "2024-01-15",
      expiryDate: "2026-01-15",
      status: "Active",
      type: "Critical"
    },
    {
      id: 2,
      name: "Pediatric Advanced Life Support",
      issueDate: "2023-06-20",
      expiryDate: "2025-06-20",
      status: "Expiring Soon",
      type: "Specialized"
    },
    {
      id: 3,
      name: "Medication Administration",
      issueDate: "2024-03-10",
      expiryDate: "2025-03-10",
      status: "Active",
      type: "Core",
    }
  ];

  const availableCourses = [
    {
      id: 1,
      title: "Advanced Wound Care Management",
      duration: "6 hours",
      rating: 4.8,
      enrolled: 234,
      category: "Clinical Skills",
      level: "Advanced",
      credits: 6
    },
    {
      id: 2,
      title: "Leadership in Healthcare",
      duration: "8 hours",
      rating: 4.9,
      enrolled: 156,
      category: "Leadership",
      level: "Intermediate",
      credits: 8
    },
    {
      id: 3,
      title: "Mental Health First Aid",
      duration: "4 hours",
      rating: 4.7,
      enrolled: 189,
      category: "Mental Health",
      level: "Beginner",
      credits: 4
    }
  ];

  const notifications = [
    {
      id: 1,
      message: "ACLS certification expires in 30 days",
      type: "warning",
      time: "2 hours ago"
    },
    {
      id: 2,
      message: "New course: Advanced Pharmacology available",
      type: "info",
      time: "1 day ago"
    },
    {
      id: 3,
      message: "Congratulations! You completed Infection Control",
      type: "success",
      time: "3 days ago"
    }
  ];



  const handleEnrollCourse = (courseId) => {
    notification.success({
      message: 'Enrollment Successful',
      description: 'You have been enrolled in the course successfully!',
    });
  };

  const renderDashboard = () => (
    <div>
      {/* Stats Overview */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Courses Completed"
              value={12}
              prefix={<CheckCircleOutlined style={{ color: '#52c41a' }} />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Active Certifications"
              value={8}
              prefix={<TrophyOutlined style={{ color: '#faad14' }} />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Learning Hours"
              value={145}
              prefix={<ClockCircleOutlined style={{ color: '#1890ff' }} />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Completion Rate"
              value={userProfile.completionRate}
              suffix="%"
              prefix={<BarChartOutlined style={{ color: '#722ed1' }} />}
            />
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]}>
        {/* Current Courses */}
        <Col xs={24} lg={16}>
          <Card title="Current Courses" extra={<Button type="link">View All</Button>}>
            <List
              itemLayout="horizontal"
              dataSource={recentCourses}
              renderItem={course => (
                <List.Item
                  actions={[
                    <Button 
                      type="primary" 
                      icon={<PlayCircleOutlined />}
                      disabled={course.status === 'Completed'}
                    >
                      {course.status === 'Completed' ? 'Completed' : 'Continue'}
                    </Button>
                  ]}
                >
                  <List.Item.Meta
                    title={course.title}
                    description={
                      <Space direction="vertical" size="small" style={{ width: '100%' }}>
                        <Space>
                          <Tag color="blue">{course.category}</Tag>
                          <Text type="secondary">{course.duration}</Text>
                          <Text type="secondary">Due: {course.dueDate}</Text>
                        </Space>
                        <Progress 
                          percent={course.progress} 
                          size="small"
                          status={course.progress === 100 ? 'success' : 'active'}
                        />
                      </Space>
                    }
                  />
                </List.Item>
              )}
            />
          </Card>
        </Col>

        {/* Profile & Notifications */}
        <Col xs={24} lg={8}>
          <Card title="Profile Overview" style={{ marginBottom: 16 }}>
            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
              <div style={{ textAlign: 'center' }}>
                <Avatar size={64} icon={<UserOutlined />} />
                <Title level={4} style={{ margin: '8px 0 4px' }}>{userProfile.name}</Title>
                <Text type="secondary">{userProfile.role}</Text>
                <br />
                <Text type="secondary">{userProfile.department}</Text>
              </div>
              <div>
                <Text strong>Overall Progress</Text>
                <Progress percent={userProfile.completionRate} />
              </div>
            </Space>
          </Card>

          <Card title="Recent Notifications">
            <List
              size="small"
              dataSource={notifications}
              renderItem={notification => (
                <List.Item>
                  <List.Item.Meta
                    avatar={<Badge status={notification.type === 'warning' ? 'warning' : notification.type === 'success' ? 'success' : 'processing'} />}
                    title={<Text style={{ fontSize: '12px' }}>{notification.message}</Text>}
                    description={<Text type="secondary" style={{ fontSize: '11px' }}>{notification.time}</Text>}
                  />
                </List.Item>
              )}
            />
          </Card>
        </Col>
      </Row>
    </div>
  );

  const renderCourses = () => (
    <Card title="My Courses">
      <Tabs defaultActiveKey="current">
        <TabPane tab="Current Courses" key="current">
          <List
            grid={{ gutter: 16, xs: 1, sm: 2, lg: 3 }}
            dataSource={recentCourses}
            renderItem={course => (
              <List.Item>
                <Card 
                  size="small"
                  actions={[
                    <Button type="primary" icon={<PlayCircleOutlined />}>
                      {course.status === 'Completed' ? 'Review' : 'Continue'}
                    </Button>
                  ]}
                >
                  <Card.Meta
                    title={course.title}
                    description={
                      <Space direction="vertical" size="small" style={{ width: '100%' }}>
                        <Tag color="blue">{course.category}</Tag>
                        <Text type="secondary">{course.duration}</Text>
                        <Progress percent={course.progress} size="small" />
                      </Space>
                    }
                  />
                </Card>
              </List.Item>
            )}
          />
        </TabPane>
        <TabPane tab="Completed" key="completed">
          <Text type="secondary">Your completed courses will appear here.</Text>
        </TabPane>
      </Tabs>
    </Card>
  );

  const renderCertifications = () => (
    <Card title="My Certifications" extra={<Button type="primary">Request New Certification</Button>}>
      <Table
        dataSource={certifications}
        columns={[
          {
            title: 'Certification',
            dataIndex: 'name',
            key: 'name',
            render: (text, record) => (
              <Space>
                <TrophyOutlined style={{ color: '#faad14' }} />
                <div>
                  <div>{text}</div>
                  <Text type="secondary" style={{ fontSize: '12px' }}>{record.type}</Text>
                </div>
              </Space>
            )
          },
          {
            title: 'Issue Date',
            dataIndex: 'issueDate',
            key: 'issueDate'
          },
          {
            title: 'Expiry Date',
            dataIndex: 'expiryDate',
            key: 'expiryDate'
          },
          {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            render: (status) => (
              <Tag color={status === 'Active' ? 'green' : 'orange'}>
                {status}
              </Tag>
            )
          },
          {
            title: 'Action',
            key: 'action',
            render: () => (
              <Button type="link" size="small">View Certificate</Button>
            )
          }
        ]}
        pagination={false}
      />
    </Card>
  );

  const renderBrowseCourses = () => (
    <div>
      <Card style={{ marginBottom: 16 }}>
        <Row gutter={16}>
          <Col xs={24} sm={12} lg={8}>
            <Input.Search
              placeholder="Search courses..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ width: '100%' }}
            />
          </Col>
          <Col xs={24} sm={12} lg={8}>
            <Select placeholder="Category" style={{ width: '100%' }}>
              <Select.Option value="clinical">Clinical Skills</Select.Option>
              <Select.Option value="safety">Safety</Select.Option>
              <Select.Option value="leadership">Leadership</Select.Option>
              <Select.Option value="soft-skills">Soft Skills</Select.Option>
            </Select>
          </Col>
          <Col xs={24} sm={12} lg={8}>
            <Select placeholder="Level" style={{ width: '100%' }}>
              <Select.Option value="beginner">Beginner</Select.Option>
              <Select.Option value="intermediate">Intermediate</Select.Option>
              <Select.Option value="advanced">Advanced</Select.Option>
            </Select>
          </Col>
        </Row>
      </Card>

      <Row gutter={[16, 16]}>
        {availableCourses.map(course => (
          <Col xs={24} sm={12} lg={8} key={course.id}>
            <Card
              cover={
                <div style={{ height: 120, background: '#f0f2f5', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <BookOutlined style={{ fontSize: 48, color: '#1890ff' }} />
                </div>
              }
              actions={[
                <Button 
                  type="primary" 
                  onClick={() => handleEnrollCourse(course.id)}
                  style={{ width: '90%' }}
                >
                  Enroll Now
                </Button>
              ]}
            >
              <Card.Meta
                title={course.title}
                description={
                  <Space direction="vertical" size="small" style={{ width: '100%' }}>
                    <Space>
                      <Tag color="blue">{course.category}</Tag>
                      <Tag>{course.level}</Tag>
                    </Space>
                    <Space>
                      <StarOutlined style={{ color: '#faad14' }} />
                      <Text>{course.rating}</Text>
                      <Text type="secondary">({course.enrolled} enrolled)</Text>
                    </Space>
                    <Space>
                      <ClockCircleOutlined />
                      <Text>{course.duration}</Text>
                      <Text type="secondary">{course.credits} Credits</Text>
                    </Space>
                  </Space>
                }
              />
            </Card>
          </Col>
        ))}
      </Row>
    </div>
  );

  const renderContent = () => {
    switch (selectedMenuItem) {
      case 'courses':
        return renderCourses();
      case 'certifications':
        return renderCertifications();
      case 'browse':
        return renderBrowseCourses();
      case 'team':
        return <Card><Text>Team progress features coming soon...</Text></Card>;
      default:
        return renderDashboard();
    }
  };

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Layout>
        {/* <Header style={{ 
          background: '#fff', 
          padding: '0 24px', 
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <Title level={3} style={{ margin: 0 }}>
            {menuItems.find(item => item.key === selectedMenuItem)?.label || 'Dashboard'}
          </Title>
          
          <Space size="large">
            <Badge count={3}>
              <Button type="text" icon={<BellOutlined />} />
            </Badge>
            <Dropdown overlay={userMenu} placement="bottomRight">
              <Button type="text">
                <Space>
                  <Avatar size="small" icon={<UserOutlined />} />
                  {userProfile.name}
                </Space>
              </Button>
            </Dropdown>
          </Space>
        </Header> */}

        <Content style={{ padding: '24px', background: '#f0f2f5' }}>
          {renderContent()}
        </Content>
      </Layout>
    </Layout>
  );
};

export default HospitalTalentDashboard;